import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Lock } from "lucide-react";

const SecuredReport = () => {
  const { reportToken } = useParams();
  const [password, setPassword] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // reportToken from URL params will be either UUID or slug
    if (!reportToken) {
      navigate('/', { replace: true });
      return;
    }

    // Check if already verified in this session
    const verified = sessionStorage.getItem(`report_verified_${reportToken}`);
    if (verified) {
      try {
        const data = JSON.parse(verified);
        navigateToReport(data);
      } catch (e) {
        sessionStorage.removeItem(`report_verified_${reportToken}`);
      }
    }
  }, [reportToken, navigate]);

  const navigateToReport = (reportData: any) => {
    const { band, score, answers, quizSubmissionId, leadSubmissionId } = reportData;
    
    if (!band) {
      toast({
        title: "Błąd",
        description: "Brak danych raportu",
        variant: "destructive"
      });
      return;
    }

    navigate(`/raport-${band}`, { 
      state: { 
        score, 
        band, 
        answers, 
        quizSubmissionId, 
        leadSubmissionId,
        reportToken 
      },
      replace: true 
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    setError('');

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('verify-report-access', {
        body: { reportToken, password }
      });

      // Jeśli funkcja zwróci błąd HTTP, Supabase ustawi invokeError
      if (invokeError) {
        console.error('Verification invoke error:', invokeError, data);

        let serverMessage: string | undefined;
        try {
          const context = (invokeError as any).context;
          if (context?.json) {
            const body = await context.json();
            serverMessage = body?.error || body?.message;
          } else if (context?.body) {
            serverMessage = (context.body as any)?.error || (context.body as any)?.message;
          }
        } catch (parseError) {
          console.error('Failed to parse edge function error body', parseError);
        }

        setError(serverMessage || invokeError.message || 'Wystąpił błąd podczas weryfikacji raportu');
        return;
      }

      if (!data) {
        setError('Brak odpowiedzi z funkcji raportu');
        return;
      }

      if ((data as any).error) {
        setError((data as any).error);
        return;
      }

      if ((data as any).success) {
        const reportData = (data as any).reportData;
        // Save verification in session storage
        sessionStorage.setItem(`report_verified_${reportToken}`, JSON.stringify(reportData));
        navigateToReport(reportData);
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setError(err.message || 'Wystąpił błąd podczas weryfikacji');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5">
        <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Zabezpieczony Raport</CardTitle>
          <CardDescription>
            Wprowadź hasło dostępu aby zobaczyć swój raport
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">Hasło dostępu</Label>
              <Input
                id="password"
                type="text"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Wpisz 6-cyfrowe hasło"
                maxLength={6}
                pattern="[0-9]*"
                inputMode="numeric"
                required
                autoFocus
                className="text-center text-lg tracking-widest"
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isVerifying || password.length !== 6}
            >
              {isVerifying ? 'Sprawdzanie...' : 'Pokaż raport'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
    </>
  );
};

export default SecuredReport;
